import React from "react"
import { useTranslation } from "react-i18next"
import Drawer from "./Drawer"
import { getUnifiedStatus } from "./GuestList"
import styles from "../EventDashboardV2.module.scss"
import { useAppSelector } from "../../../redux/store"

interface GuestDetailDrawerProps {
  guest: any
  show: boolean
  setShow: (v: boolean) => void
  onEdit: (guest: any) => void
}

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
)

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
)

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

type PillColor = "green" | "blue" | "red" | "gray"
const PILL_CLASS: Record<PillColor, string> = {
  green: "detailPillGreen",
  blue: "detailPillBlue",
  red: "detailPillRed",
  gray: "detailPillGray",
}

const getWaPillInfo = (waStatus: number): { label: string; color: PillColor } => {
  switch (waStatus) {
    case 1: return { label: "Sent", color: "blue" }
    case 2: return { label: "Delivered", color: "green" }
    case 3: return { label: "Received", color: "green" }
    case 4: return { label: "Read", color: "green" }
    case 5: return { label: "Failed", color: "red" }
    case 6: return { label: "Deleted", color: "gray" }
    default: return { label: "Not sent", color: "gray" }
  }
}

const getEmailPillInfo = (isSent: boolean): { label: string; color: PillColor } => {
  return isSent
    ? { label: "Sent", color: "blue" }
    : { label: "Not sent", color: "gray" }
}

const getRsvpText = (rsvpStatus: number, waStatus: number): { label: string; color: PillColor } => {
  if (rsvpStatus === 1) return { label: "Confirmed", color: "green" }
  if (rsvpStatus === 2) return { label: "Declined", color: "red" }
  if (rsvpStatus === 3) return { label: "Invalid contact", color: "red" }

  // Can only be "awaiting response" if an invitation was actually delivered
  if (waStatus >= 2 && waStatus <= 4) return { label: "Awaiting response", color: "blue" }
  if (waStatus === 1) return { label: "Sent — awaiting delivery", color: "gray" }
  if (waStatus === 5) return { label: "Delivery failed", color: "red" }

  return { label: "Not invited yet", color: "gray" }
}

const GuestDetailDrawer: React.FC<GuestDetailDrawerProps> = ({
  guest,
  show,
  setShow,
  onEdit,
}) => {
  const { t } = useTranslation()
  const isOpenEvent = useAppSelector(
    (state) => state.events.currentEvent?.is_open_event === 1,
  )

  if (!guest || !guest.id) return null

  const fullName = [guest.suffix, guest.first_name, guest.last_name]
    .filter(Boolean)
    .join(" ")

  const initials = [guest.first_name, guest.last_name]
    .filter(Boolean)
    .map((n: string) => n[0]?.toUpperCase())
    .join("")

  const phone = guest.phone ? guest.phone.split("|")[0] : ""
  const email = guest.email || ""
  const waStatus = guest.whatsapp_invitation_status ?? 0
  const waPill = getWaPillInfo(waStatus)
  const emailPill = email ? getEmailPillInfo(!!guest.is_email_sent) : null

  const unified = getUnifiedStatus(
    guest.status,
    waStatus,
    guest.invitation_failure_type,
    isOpenEvent,
  )
  const rsvp = getRsvpText(guest.status, waStatus)

  const actual = guest.actual_additional_guests ?? 0
  const max = guest.quantity_members ?? 1
  const inviteText = t("eventDashboardV2.messages.inviteCountProgress", {
    current: actual + 1,
    max: max + 1,
  })

  const zone = guest.zone || "—"

  return (
    <Drawer
      open={show}
      onClose={() => setShow(false)}
      title={t("eventDashboardV2.labels.guestDetails")}
      footer={
        <button
          className={styles.detailEditBtn}
          onClick={() => onEdit(guest)}
        >
          <EditIcon />
          {t("eventDashboardV2.messages.editGuest")}
        </button>
      }
    >
      <div className={styles.detailSection}>
        <div className={styles.detailGuestHeader}>
          <div className={styles.detailAvatar}>{initials || "?"}</div>
          <div>
            <div className={styles.detailGuestName}>{fullName || "—"}</div>
            <div className={styles.detailGuestId}>
              {t("eventDashboardV2.messages.guestIdWithHash", { id: guest.id })}
            </div>
          </div>
        </div>
        <div className={styles.detailRow}>
            <span className={styles.detailLabel}>{t("eventDashboardV2.labels.status")}</span>
          <span
            className={`${styles.detailPill} ${styles[PILL_CLASS[unified.dotColor === "Green" ? "green" : unified.dotColor === "Blue" ? "blue" : unified.dotColor === "Red" ? "red" : "gray"]]}`}
          >
            {t(`eventDashboardV2.status.${unified.text}`, unified.text)}
          </span>
        </div>
      </div>

      <div className={styles.detailSection}>
        <div className={styles.detailSectionTitle}>{t("eventDashboardV2.labels.channels")}</div>
        {phone && (
          <div className={styles.detailRow}>
            <div className={styles.detailRowLeft}>
              <WhatsAppIcon />
              <span className={styles.detailLabel}>{t("eventDashboardV2.labels.phone")}</span>
              <span className={styles.detailValue}>{phone}</span>
            </div>
            <span className={`${styles.detailPill} ${styles[PILL_CLASS[waPill.color]]}`}>
              {t(`eventDashboardV2.status.${waPill.label}`, waPill.label)}
            </span>
          </div>
        )}
        {email && (
          <div className={styles.detailRow}>
            <div className={styles.detailRowLeft}>
              <MailIcon />
              <span className={styles.detailLabel}>{t("eventDashboardV2.labels.email")}</span>
              <span className={styles.detailValue}>{email}</span>
            </div>
            {emailPill && (
              <span className={`${styles.detailPill} ${styles[PILL_CLASS[emailPill.color]]}`}>
                {t(`eventDashboardV2.status.${emailPill.label}`, emailPill.label)}
              </span>
            )}
          </div>
        )}
        {!phone && !email && (
          <div className={styles.detailRow}>
            <span className={styles.detailValue}>{t("eventDashboardV2.messages.noChannelsConfigured")}</span>
          </div>
        )}
      </div>

      <div className={styles.detailSection}>
        <div className={styles.detailSectionTitle}>{t("eventDashboardV2.labels.rsvp")}</div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>{t("eventDashboardV2.labels.response")}</span>
          <span className={`${styles.detailPill} ${styles[PILL_CLASS[rsvp.color]]}`}>
            {t(`eventDashboardV2.status.${rsvp.label}`, rsvp.label)}
          </span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>{t("eventDashboardV2.table.invites")}</span>
          <span className={styles.detailValue}>{inviteText}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>{t("eventDashboardV2.labels.zone")}</span>
          <span className={styles.detailValue}>{zone}</span>
        </div>
      </div>
    </Drawer>
  )
}

export default GuestDetailDrawer
